import { Typography, Divider, Container } from '@cleartrip/ct-design-components'
import { memo, useMemo, useState } from 'react'
import ImageBlock from '@/components/imageBlock'
import { filterTypes } from '@/constants/Srp'
import { FilterData as FilterDataType, PossibleFilterTypes } from '@/types/srp'
import { formatCurrency } from '@/utils'
import { isValidPrice } from '@/utils/general'
import { getFlightLogoUrl, shouldShowAirlineLogoBorder } from '@/utils/images'
import CheckboxItem from './checkboxItem'
import FilterJourneyInfo from './FilterJourneyInfo'

type AirlineOption = {
  label: string
  value: string
  priceLabel?: string
  priceLabelADT?: string
}

type AirlineFilterProps = {
  onFilterChange: (
    type: PossibleFilterTypes,
    filtersData: FilterDataType,
    showMultiAirlineItineraries?: boolean
  ) => void
  airlineFilter: {
    options: AirlineOption[]
    selectedValues: string[]
    showMultiAirlineItineraries?: boolean
  }
  from?: string
  showPerAdultPricing: boolean
  to?: string
  isRT?: boolean
  hideMultiAirlineBlock?: boolean
  isRevamp?: boolean
}

const AirlinesFilter = ({
  onFilterChange,
  airlineFilter,
  showPerAdultPricing,
  from,
  to,
  isRT,
  hideMultiAirlineBlock = false,
  isRevamp = false,
}: AirlineFilterProps) => {
  const [showAllAirlines, setShowAllAirlines] = useState(false)
  const { options, selectedValues = [], showMultiAirlineItineraries = false } = airlineFilter || {}
  const [showMultiAirline, setShowMultiAirline] = useState(showMultiAirlineItineraries)

  const onAirlineClick = (value: string) => {
    const updatedAirlineSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(selectedValue => selectedValue !== value)
      : [...selectedValues, value]
    onFilterChange(filterTypes.AIRLINE_FILTER, {
      selectedValues: updatedAirlineSelectedValues,
      showMultiAirlineItineraries: showMultiAirlineItineraries,
    })
  }

  const toggleMultiAirlineChecked = () => {
    onFilterChange(filterTypes.AIRLINE_FILTER, {
      selectedValues: selectedValues,
      showMultiAirlineItineraries: !showMultiAirline,
    })
    setShowMultiAirline(!showMultiAirline)
  }

  const toggleShowAllAirlines = () => {
    setShowAllAirlines(!showAllAirlines)
  }

  const sortedOptions = useMemo(() => {
    const airlines = [...(options || [])]
    return airlines.sort((a: AirlineOption, b: AirlineOption) => {
      return (a?.label || '').localeCompare(b?.label || '')
    })
  }, [options])

  const displayedOptions = showAllAirlines ? sortedOptions : sortedOptions.slice(0, 3)
  const remainingCount = sortedOptions.length - 3

  const labelVariant = 'P2'
  const priceVariant = isRevamp ? 'P3' : 'P2'
  const logoSize = isRevamp ? 24 : 18

  return (
    <Container className={isRevamp ? 'flex flex-column' : undefined}>
      <FilterJourneyInfo
        from={from || ''}
        to={to || ''}
        isRT={!!isRT}
        isRevamp={isRevamp}
        legacyClassName='c-pointer py-3'
      />
      {!hideMultiAirlineBlock && (
        <Container className={isRevamp ? 'flex flex-column' : undefined}>
          <CheckboxItem
            label={
              <Container className='flex flex-1'>
                <Typography variant={labelVariant} className='c-pointer'>
                  Show multi-airline itineraries
                </Typography>
              </Container>
            }
            rootClassName='w-100p'
            checked={showMultiAirline}
            onClick={toggleMultiAirlineChecked}
            wrapperClassName='py-2'
          />
          {!isRevamp && <Divider className='w-100p' borderStyle='dotted' borderColor='#E6E6E6' />}
        </Container>
      )}

      {displayedOptions.map(option => {
        const { label, value, priceLabel, priceLabelADT }: AirlineOption = option
        const checked = selectedValues.includes(value)
        const displayPrice = showPerAdultPricing ? priceLabelADT : priceLabel

        return (
          <CheckboxItem
            key={value}
            label={
              <div className='flex flex-1 flex-between flex-middle'>
                <Container className='flex flex-middle'>
                  <ImageBlock
                    isCircleLogo
                    altText={value}
                    height={logoSize}
                    path={getFlightLogoUrl(value, 'circle_logo')}
                    showBorder={!isRevamp || shouldShowAirlineLogoBorder(value)}
                    width={logoSize}
                  />

                  <Typography variant={labelVariant} className='c-pointer ml-2'>
                    {label}
                  </Typography>
                </Container>

                {isValidPrice(displayPrice) && (
                  <Typography variant={priceVariant} color='subheading' className='flex'>
                    {formatCurrency(Number(displayPrice))}
                  </Typography>
                )}
              </div>
            }
            rootClassName='w-100p'
            checked={checked}
            onClick={() => onAirlineClick(value)}
            wrapperClassName='py-2 checkbox-hover-border'
          />
        )
      })}

      {!showAllAirlines && remainingCount > 0 && (
        <div className={isRevamp ? undefined : 'mt-2 mb-5 '}>
          <Typography
            variant='L2'
            className='c-pointer c-primary'
            isClickable={true}
            onClick={toggleShowAllAirlines}
            color='link'
          >
            Show {remainingCount} more airline{remainingCount !== 1 ? 's' : ''}
          </Typography>
        </div>
      )}

      {showAllAirlines && sortedOptions.length > 3 && (
        <div className={isRevamp ? undefined : 'my-4'}>
          <Typography
            variant='L2'
            className='c-pointer c-primary'
            isClickable={true}
            color='link'
            onClick={toggleShowAllAirlines}
          >
            Show less
          </Typography>
        </div>
      )}
    </Container>
  )
}

export default AirlinesFilter
